/**
 * Defaults for the login screen configuration fields (UX-800). Shared by the Branding page
 * admin UI (`branding-visual-login.tsx`, which shows these as placeholders) and the login
 * page itself (UX-795, which falls back to them when the brand hasn't set a value) — both
 * need to agree on the same strings and limits.
 */
export const WELCOME_HEADING_DEFAULT = 'Welcome!';
export const WELCOME_BODY_DEFAULT = 'Sign-in to view, download and share your listing media.';
export const SIGN_IN_LABEL_DEFAULT = 'Sign In';

export const WELCOME_HEADING_MAX = 50;
export const WELCOME_BODY_MAX = 250;
export const SIGN_IN_LABEL_MAX = 20;

/**
 * API-549 deliberately descoped `loginWelcomeHeading`/`loginWelcomeBody`/`loginSignInButtonLabel`
 * as brand columns — they stay hardcoded React text on the API side permanently (not a future
 * ticket, a settled decision). UX-800 QA failed on that basis, so per product decision these three
 * live in the brand's `theme` CSS custom properties instead (the same store `--login-panel-opacity`
 * and `--login-logo-width` already use) and are never sent to the `brands` PATCH surface directly.
 */
export const LOGIN_WELCOME_HEADING_VAR = '--login-welcome-heading';
export const LOGIN_WELCOME_BODY_VAR = '--login-welcome-body';
export const LOGIN_SIGN_IN_LABEL_VAR = '--login-sign-in-label';

/**
 * Theme values round-trip through an actual `<style>` element (`Theme.ts#applyTheme`), which
 * naively concatenates `${name}: ${value};` with no escaping. A raw `;` or `}` in free text would
 * terminate the declaration or its enclosing `BODY { }` block early and could swallow whatever
 * theme rule follows it. Wrapping the value as a quoted CSS string token makes it immune to that —
 * the tokenizer treats everything between the (escaped) quotes as one opaque unit.
 */
export function encodeCssText(value: string): string {
    const escaped = value
        .replace(/\\/g, '\\\\')
        .replace(/"/g, '\\"')
        .replace(/\r\n|\r|\n/g, '\\A ');
    return `"${escaped}"`;
}

/** Reverses `encodeCssText`. Returns '' for anything that isn't a quoted string (unset, legacy). */
export function decodeCssText(raw: string | undefined | null): string {
    if (!raw) {
        return '';
    }
    const match = raw.match(/^"([\s\S]*)"$/);
    if (!match) {
        return '';
    }
    return match[1]
        .replace(/\\A ?/g, '\n')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\');
}

/**
 * Resolves a stored welcome text for display. An unset (or legacy, unquoted) property means
 * "never configured" and yields `fallback`; a stored quoted string — including the empty `""`
 * that `encodeCssText('')` produces — is the brand's explicit choice, so an empty one stays
 * empty and the login page hides that element.
 */
export function resolveCssText(raw: string | undefined | null, fallback: string): string {
    if (!raw || !/^"[\s\S]*"$/.test(raw)) {
        return fallback;
    }
    return decodeCssText(raw);
}
